import { Box, Card, Center, DataList, Grid, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { type ActionSuggestionPublic, type IndicatorSuggestionPublic, LibraryService } from '@/client/index.ts';
import { ResultNotFound } from '@/components/common/ResultNotFound';
import { ScrollToTopButton } from '@/components/common/ScrollToTopButton';
import { Route } from '@/routes/_layout/$slug/library.tsx';

import LibrarySuggestionDrawer from './LibrarySuggestionDrawer';

const DEFAULT_PER_PAGE = 20;

export function LibraryCards() {
  const { ref, inView } = useInView();
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { type, filter, sort, search } = Route.useSearch();
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();

  const [isIndicatorDrawerOpen, setIsIndicatorDrawerOpen] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState<
    IndicatorSuggestionPublic | ActionSuggestionPublic | undefined
  >();

  const handleSelectedSuggestion = (item: IndicatorSuggestionPublic | ActionSuggestionPublic) => {
    setSelectedIndicator(item);
    setIsIndicatorDrawerOpen(true);
  };

  const { data, error, fetchNextPage, isFetching, hasNextPage } = useInfiniteQuery({
    queryKey: [`${type}_suggestions`, { filter, sort, search }],
    queryFn: async ({
      pageParam,
    }): Promise<{
      data: Array<IndicatorSuggestionPublic> | Array<ActionSuggestionPublic>;
      previousId: number | undefined;
      nextId: number | undefined;
    }> => {
      const _read = type === 'indicators' ? LibraryService.readLibraryIndicators : LibraryService.readLibraryActions;
      const res = await _read({
        skip: (pageParam - 1) * DEFAULT_PER_PAGE,
        limit: DEFAULT_PER_PAGE,
        companySlug: slug,
        filter: filter,
        sort: sort,
        search: search,
      });

      // Calculate previous and next page IDs
      const totalPages = Math.ceil(res.count / DEFAULT_PER_PAGE);

      const previousId = pageParam > 1 ? pageParam - 1 : undefined;
      const nextId = pageParam < totalPages ? pageParam + 1 : undefined;

      return {
        data: res.data,
        previousId,
        nextId,
      };
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => firstPage.previousId,
    getNextPageParam: (lastPage) => lastPage.nextId,
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  useEffect(() => {
    if (containerRef.current) {
      //@ts-ignore
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    queryClient.resetQueries({ queryKey: ['actions_suggestions'] });
    queryClient.resetQueries({ queryKey: ['indicators_suggestions'] });
  }, [type, filter, sort, search, queryClient]);

  const formatImage = (url: string | null | undefined) => {
    if (!url) return '';
    return `https://${import.meta.env.VITE_AWS_BUCKET_REFERENTIAL}.${import.meta.env.VITE_AWS_ENDPOINT_URL}/${url}`;
  };
  const defaultsImages = {
    Economique: formatImage('28d49634-40dd-47d9-bc06-0ddac76f1b2c.png'),
    Envrionnement: formatImage('88574ac4-503c-4882-a697-479f53f64820.png'),
    Gouvernance: formatImage('f62cf259-decc-4583-8865-7e986b6bcc86.png'),
    Social: formatImage('725d5006-cfe9-49dc-a62b-ed36b38d79ee.png'),
    'Territoire & Intérêt général': formatImage('dec5597f-a757-4a1e-aea0-41cdcd8ac288.png'),
    default: '/assets/images/default-image.png',
  };

  if (error) {
    return (
      <Center m={6}>
        <Text color="red.500">An error occurred while fetching the data.</Text>
      </Center>
    );
  }

  return (
    <>
      <LibrarySuggestionDrawer
        content={selectedIndicator}
        setIsOpen={setIsIndicatorDrawerOpen}
        isOpen={isIndicatorDrawerOpen}
      />
      <Box overflowY={'auto'} maxH={'calc(100vh - 70px)'} ref={containerRef}>
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            xl: 'repeat(3, 1fr)',
            '2xl': 'repeat(4, 1fr)',
          }}
          gap="4"
          p={4}
        >
          {data?.pages.map((page) => (
            <Fragment key={page.nextId}>
              {page.data.map((item) => (
                <Card.Root
                  overflow="hidden"
                  cursor={'pointer'}
                  key={item.id}
                  _hover={{ boxShadow: 'xl' }}
                  shadowColor="primary"
                  transition="box-shadow 0.2s ease-in-out"
                  onClick={() => handleSelectedSuggestion(item)}
                >
                  <Image
                    px={6}
                    pt={6}
                    src={
                      formatImage(item.image_path) ||
                      defaultsImages[(item.pillar_suggestion_name as keyof typeof defaultsImages) ?? 'default']
                    }
                  />
                  <Card.Body gap="2">
                    <Card.Title>{item.name}</Card.Title>
                    <Card.Description
                      lineClamp="5"
                      {...(!item.description && {
                        color: 'gray.500',
                        fontStyle: 'italic',
                      })}
                    >
                      {!!item.description && item.description}
                      {!item.description &&
                        type === 'action' &&
                        t('suggestion.columnsHeader.description.action.noContent')}
                      {!item.description &&
                        type !== 'action' &&
                        t('suggestion.columnsHeader.description.indicator.noContent')}
                    </Card.Description>
                  </Card.Body>
                  <Card.Footer gap="2">
                    <DataList.Root orientation="horizontal" divideY="1px" maxW="md">
                      <DataList.Item>
                        <DataList.ItemLabel>{t('library.columnsHeader.pillar')}</DataList.ItemLabel>
                        <DataList.ItemValue>{item.pillar_suggestion_name}</DataList.ItemValue>
                      </DataList.Item>
                      <DataList.Item pt="4">
                        <DataList.ItemLabel>{t('library.columnsHeader.commitment')}</DataList.ItemLabel>
                        <DataList.ItemValue>{item.commitment_suggestion_name}</DataList.ItemValue>
                      </DataList.Item>
                      {'lt_indicator_category' in item && (
                        <DataList.Item pt="4">
                          <DataList.ItemLabel>{t('library.columnsHeader.type')}</DataList.ItemLabel>
                          <DataList.ItemValue>{item.lt_indicator_category}</DataList.ItemValue>
                        </DataList.Item>
                      )}
                      {'lt_indicator_other_unit' in item && (
                        <DataList.Item pt="4">
                          <DataList.ItemLabel>{t('library.columnsHeader.unit')}</DataList.ItemLabel>
                          <DataList.ItemValue>{item.lt_indicator_other_unit}</DataList.ItemValue>
                        </DataList.Item>
                      )}
                    </DataList.Root>
                  </Card.Footer>
                </Card.Root>
              ))}
            </Fragment>
          ))}
        </Grid>
        {hasNextPage && (
          <Box my={4} ref={ref} p={4}>
            <Center>
              <VStack colorPalette="teal">
                <Spinner color="primary.600" />
                <Text color="primary.600">Loading...</Text>
              </VStack>
            </Center>
          </Box>
        )}
      </Box>
      {!isFetching && data?.pages[0].data.length === 0 && <ResultNotFound />}
      <ScrollToTopButton scrollContainerRef={containerRef} />
    </>
  );
}

export default LibraryCards;
